import React, { useState } from "react";
import {
  Typography,
  Table,
  Space,
  DatePicker,
  Button,
  Statistic,
  Row,
  Col,
  Input,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadExpensesReport,
  useReportsServiceGetExpensesReport,
} from "api/queries";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const ExpenseReport: React.FC = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");
  const [search, setSearch] = useState("");
  const { data, isLoading, refetch } = useReportsServiceGetExpensesReport({
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    search,
  });
  const { mutate: downloadReport, isPending: isDownloading } = useReportsServiceDownloadExpensesReport();

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
    refetch();
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    refetch();
  };

  const handleDownload = () => {
    downloadReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        search,
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "expense_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  type KPIs = {
    totalExpenses?: number;
    totalAmount?: number;
  };
  const kpis: KPIs = data?.kpis || {};
  const tableData = (data?.items || []).map((item: any) => ({
    ...item,
    key: item.expenseId,
  }));

  const columns = [
    { title: "Date", dataIndex: "expenseDate", key: "expenseDate", render: (date: string) => dayjs(date).format("YYYY-MM-DD") },
    { title: "Expense Name", dataIndex: "expenseName", key: "expenseName" },
    { title: "Description", dataIndex: "expenseDescription", key: "expenseDescription", render: (desc: string | null) => desc || "-" },
    { title: "Amount", dataIndex: "expenseAmount", key: "expenseAmount", render: (value: number) => value?.toLocaleString?.() ?? value },
    { title: "Category", dataIndex: "categoryName", key: "categoryName" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Expense Report</Title>
          <Space>
            <Input.Search
              allowClear
              placeholder="Search expenses..."
              style={{ width: 260 }}
              value={search}
              onChange={handleSearchChange}
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 160 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic title="Total Expenses" value={kpis?.totalExpenses ?? 0} />
          </Col>
          <Col span={8}>
            <Statistic title="Total Amount" value={kpis?.totalAmount?.toLocaleString?.() ?? 0} />
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={tableData}
          loading={isLoading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
          bordered
        />
      </Space>
    </motion.div>
  );
};

export default ExpenseReport;
